import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

// Toast container state
let toastState = {
  toasts: [],
  listeners: [],
};

// Subscribe to toast state
const subscribe = (listener) => {
  toastState.listeners.push(listener);
  return () => {
    toastState.listeners = toastState.listeners.filter((l) => l !== listener);
  };
};

// Notify all listeners
const notify = () => {
  toastState.listeners.forEach((listener) => listener(toastState.toasts));
};

// Add toast
export const showToast = (message, type = 'info', duration = 3000) => {
  const id = Date.now();
  const toast = { id, message, type, duration };
  toastState.toasts = [...toastState.toasts, toast];
  notify();

  if (duration > 0) {
    setTimeout(() => {
      removeToast(id);
    }, duration);
  }
};

// Remove toast
const removeToast = (id) => {
  toastState.toasts = toastState.toasts.filter((t) => t.id !== id);
  notify();
};

// Toast icons
const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

// Toast colors using CSS variables
const getToastStyles = (type) => {
  const styles = {
    success: {
      bg: 'var(--color-success-light)',
      border: 'var(--color-success)',
      text: 'var(--color-success)',
      icon: 'var(--color-success)',
    },
    error: {
      bg: 'var(--color-danger-light)',
      border: 'var(--color-danger)',
      text: 'var(--color-danger)',
      icon: 'var(--color-danger)',
    },
    warning: {
      bg: 'var(--color-warning-light)',
      border: 'var(--color-warning)',
      text: 'var(--color-warning)',
      icon: 'var(--color-warning)',
    },
    info: {
      bg: 'var(--color-info-light)',
      border: 'var(--color-info)',
      text: 'var(--color-info)',
      icon: 'var(--color-info)',
    },
  };
  return styles[type] || styles.info;
};

const Toast = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribe(setToasts);
    return unsubscribe;
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => {
        const Icon = icons[toast.type] || Info;
        const styles = getToastStyles(toast.type);
        
        return (
          <div
            key={toast.id}
            className="flex items-center p-4 rounded-lg border shadow-lg animate-slide-in"
            style={{
              backgroundColor: styles.bg,
              borderColor: styles.border,
              color: styles.text,
            }}
          >
            <Icon className="w-5 h-5 mr-3" style={{ color: styles.icon }} />
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-4 p-1 rounded hover:opacity-70 transition-opacity"
              style={{ color: styles.text }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default Toast;
